'use client';
import { useEffect, useState } from 'react';
import Slider from 'react-slick';
import Image from 'next/image';

import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { VillageMedia } from '@/services/types';

import translateFromArray from '@/utils/get-translated-object';
import getMediaFilterUrl from '@/utils/get-media-filter';
import { cn } from '@/lib/utils';
import Spinner from '@/components/Spinner';

type Props = {
  handleClose: () => void;
  medias: VillageMedia[];
  goToItem: number;
  lang: string;
};

export default function GalleryModalCarousel({
  handleClose,
  medias,
  goToItem,
  lang,
}: Props) {
  const [sliderRef, setSliderRef] = useState<Slider | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [currentImageDescription, setCurrentImageDescription] = useState('');
  const [showImage, setShowImage] = useState(false);
  const [loadedSlides, setLoadedSlides] = useState<Set<number>>(new Set());

  const markSlideLoaded = (index: number) =>
    setLoadedSlides((prev) => new Set(prev).add(index));

  const settings = {
    dots: false,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    beforeChange: (current: number, next: number) => {
      setActiveSlide(next);
    },
  };

  useEffect(() => {
    const currentMedia = medias[activeSlide];
    currentMedia?.translations?.map((translation) => {
      if (translation.locale === lang) {
        setCurrentImageDescription(translation.description);
      }
    });
    // eslint-disable-next-line
  }, [activeSlide]);

  useEffect(() => {
    if (sliderRef) {
      setActiveSlide(goToItem);
      sliderRef.slickGoTo(goToItem, true);

      const waitTimer = setTimeout(() => {
        setShowImage(true);
      }, 5);

      return () => clearTimeout(waitTimer);
    }
    // eslint-disable-next-line
  }, [sliderRef]);

  return (
    <div className="block max-h-[100vh] top-[0px] left-[0px] right-0 bottom-0 margin-0 fixed w-full h-full min-h-full bg-white z-[100] p-4">
      <button
        onClick={handleClose}
        className="absolute top-[1rem] right-[1rem] mb-0 leading-[1] z-10 cursor-pointer appearance-none text-gray-500">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-10 h-10 fill-current text-gray-500"
          viewBox="0 -960 960 960">
          <path d="m256-168-88-88 224-224-224-224 88-88 224 224 224-224 88 88-224 224 224 224-88 88-224-224-224 224Z" />
        </svg>
      </button>
      <button
        onClick={sliderRef?.slickPrev}
        className="hidden lg:block absolute top-[50%] cursor-pointer left-0 translate-y-[-50%] outline-none z-10">
        <FiChevronLeft className="w-20 h-20 text-gray-500" />
      </button>
      <div className="h-full flex items-center justify-center flex-col">
        <Slider
          ref={setSliderRef}
          className="h-[50%] mt-[60px] lg:mt-0 w-full lg:w-[90%] lg:h-[calc(100%-90px)] z-10 [&>.slick-list]:h-full [&>.slick-list>.slick-track]:h-full [&>.slick-list>.slick-track>.slick-slide>div]:w-full [&>.slick-list>.slick-track>.slick-slide>div]:h-full"
          {...settings}>
          {medias.map((media, index) => {
            const translations = translateFromArray({
              lang,
              translations: media?.translations,
            });

            const imageDescription = translations?.description ?? '';

            const imageSource = getMediaFilterUrl({
              mediaFilters: media.mediaFilters,
              filterName: [
                'large_gallery_wm_2500',
                '2400w',
                '1920w',
                'large_gallery_wm_1280_webp',
              ],
            });

            const smallImageSource = getMediaFilterUrl({
              mediaFilters: media.mediaFilters,
              filterName: ['small_1-1', 'small_3-2', '480w_webp'],
            });

            const isLoaded = loadedSlides.has(index);

            return (
              <div
                key={`${media.id}-${index}`}
                className="h-full relative overflow-hidden">
                {smallImageSource && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={smallImageSource}
                    alt=""
                    aria-hidden="true"
                    className={cn(
                      'absolute inset-0 z-20 w-full h-full object-cover md:object-contain transition-opacity duration-300',
                      isLoaded
                        ? 'opacity-0 pointer-events-none'
                        : 'opacity-100',
                    )}
                    style={{ filter: 'blur(12px)' }}
                  />
                )}
                {!isLoaded && (
                  <div className="absolute inset-0 z-30 flex items-center justify-center">
                    <Spinner className="text-gray-850" />
                  </div>
                )}
                {showImage && (
                  <Image
                    src={
                      imageSource ??
                      `${process.env.NEXT_PUBLIC_S3_URI}/thumbs/large_gallery_wm_1280_webp/${media.path}${media.filename}`
                    }
                    alt={imageDescription}
                    width={100}
                    height={100}
                    loading="eager"
                    onLoad={() => markSlideLoaded(index)}
                    onError={() => markSlideLoaded(index)}
                    className={cn(
                      `w-full h-full object-cover align-middle max-w-full md:object-contain transition-opacity duration-500`,
                      isLoaded ? 'opacity-100' : 'opacity-0',
                    )}
                    sizes="100vw, (min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
                  />
                )}
              </div>
            );
          })}
        </Slider>
        <div className="min-h-0 w-full flex justify-start items-center text-black my-[20px] text-center md:w-[90%] md:justify-center desktop:flex desktop:my-[10px] desktop:justify-start">
          <small>
            {`${activeSlide + 1}/${medias.length}`} {currentImageDescription}
          </small>
        </div>
      </div>
      <button
        onClick={sliderRef?.slickNext}
        className="hidden lg:block absolute top-[50%] cursor-pointer right-0 translate-y-[-50%] outline-none z-10">
        <FiChevronRight className="w-20 h-20 text-gray-500" />
      </button>
    </div>
  );
}
